import React from 'react';

import { Selector } from '@/components/ui/inputs/selector';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';

// Tailwind requiere valores arbitrarios estáticos en el código fuente, por lo que no puede
// interpolar colors.primary.general (#B2E5FF) / colors.primary.main (#1E5591) en runtime.
// Mismo estilo de chip usado por MonthSummarySection (dashboard "Mi asistencia").
const FILTER_SELECTOR_CLASS_NAME =
  '!h-6 !rounded-full !border-0 !bg-[#B2E5FF] !text-[#1E5591] !pl-2 !pr-8 !py-0 flex items-center gap-2';

type SelectOption = { value: string; label: string };

// Props subset (not the full `useMyTeamMonthlySummaryFilters` hook result) — the
// component is purely presentational, all filter/business logic lives in the hook.
export interface MyTeamMonthlySummaryFiltersProps {
  selectedCompanyId: string;
  selectedYear: string;
  selectedMonth: string;
  selectedMonthlyCutId: string;
  companyOptions: SelectOption[];
  yearOptions: SelectOption[];
  monthOptions: SelectOption[];
  descriptionOptions: SelectOption[];
  isLoadingCutoffs: boolean;
  hasAvailableCutoffs: boolean;
  isFiltersDisabled: boolean;
  onCompanyChange: (companyId: string) => void;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
  onDescriptionChange: (monthlyCutId: string) => void;
  'data-testid'?: string;
}

function toSingleValue(value: string | string[]): string {
  return Array.isArray(value) ? (value[0] ?? '') : value;
}

// "Resumen del mes" filters panel for the "Mi equipo" dashboard (ASIS-5414):
// Empresa (single-select) / Año / Mes / Descripción, with an informative message
// + filters disablement when the selected company has no monthly cutoffs.
export const MyTeamMonthlySummaryFilters: React.FC<MyTeamMonthlySummaryFiltersProps> = ({
  selectedCompanyId,
  selectedYear,
  selectedMonth,
  selectedMonthlyCutId,
  companyOptions,
  yearOptions,
  monthOptions,
  descriptionOptions,
  isLoadingCutoffs,
  hasAvailableCutoffs,
  isFiltersDisabled,
  onCompanyChange,
  onYearChange,
  onMonthChange,
  onDescriptionChange,
  'data-testid': dataTestId,
}) => {
  const { t: translate } = useTranslation();

  return (
    <div className="flex flex-col gap-2" data-testid={dataTestId}>
      <div className="flex flex-row flex-wrap items-center gap-2">
        <Selector
          label={translate('attendance:dashboard.myTeam.monthSummary.filters.company')}
          placeholder={translate('attendance:dashboard.myTeam.monthSummary.filters.company')}
          options={companyOptions}
          value={selectedCompanyId || undefined}
          onChange={(value) => onCompanyChange(toSingleValue(value))}
          // AC (ASIS-5414): Empresa nunca se deshabilita, ni siquiera cuando la empresa
          // seleccionada no tiene cortes — solo Año/Mes/Descripción reflejan ese estado.
          disabled={false}
          isLoading={isLoadingCutoffs && companyOptions.length === 0}
          hideLabel
          chevronColor={colors.primary.main}
          chevronWrapperClassName="items-center"
          className="w-[196px]"
          selectClassName={FILTER_SELECTOR_CLASS_NAME}
          data-testid={`${dataTestId}-company-selector`}
        />
        <Selector
          label={translate('attendance:dashboard.myTeam.monthSummary.filters.year')}
          placeholder={translate('attendance:dashboard.myTeam.monthSummary.filters.year')}
          options={yearOptions}
          value={selectedYear || undefined}
          onChange={(value) => onYearChange(toSingleValue(value))}
          disabled={isFiltersDisabled}
          isLoading={isLoadingCutoffs}
          hideLabel
          chevronColor={colors.primary.main}
          chevronWrapperClassName="items-center"
          className="w-[196px]"
          selectClassName={FILTER_SELECTOR_CLASS_NAME}
          data-testid={`${dataTestId}-year-selector`}
        />
        <Selector
          label={translate('attendance:dashboard.myTeam.monthSummary.filters.month')}
          placeholder={translate('attendance:dashboard.myTeam.monthSummary.filters.month')}
          options={monthOptions}
          value={selectedMonth || undefined}
          onChange={(value) => onMonthChange(toSingleValue(value))}
          disabled={isFiltersDisabled}
          isLoading={isLoadingCutoffs}
          hideLabel
          chevronColor={colors.primary.main}
          chevronWrapperClassName="items-center"
          className="w-[196px]"
          selectClassName={FILTER_SELECTOR_CLASS_NAME}
          data-testid={`${dataTestId}-month-selector`}
        />
        <Selector
          label={translate('attendance:dashboard.myTeam.monthSummary.filters.description')}
          placeholder={translate('attendance:dashboard.myTeam.monthSummary.filters.description')}
          options={descriptionOptions}
          value={selectedMonthlyCutId || undefined}
          onChange={(value) => onDescriptionChange(toSingleValue(value))}
          disabled={isFiltersDisabled}
          isLoading={isLoadingCutoffs}
          hideLabel
          chevronColor={colors.primary.main}
          chevronWrapperClassName="items-center"
          className="w-[196px]"
          selectClassName={FILTER_SELECTOR_CLASS_NAME}
          data-testid={`${dataTestId}-description-selector`}
        />
      </div>

      {!hasAvailableCutoffs && (
        <Text
          variant="small-medium"
          color={colors.feedback.alert400}
          data-testid={`${dataTestId}-no-cutoffs-message`}
        >
          {translate('attendance:dashboard.myTeam.monthSummary.filters.noCutoffsAvailable')}
        </Text>
      )}
    </div>
  );
};

export default MyTeamMonthlySummaryFilters;
